import {
  ScopeChangeRequest,
  type ScopeChangeRequestItem,
} from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import { type ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { validateElements } from '@/utils/validx-util'
import { randomUUID } from '@/utils/uuid-util'
import { required, validationContext } from 'validx'

export class ScopeChangeEditCustomItemsDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable
  accessor scopeChangeRequest = ScopeChangeRequest.none

  @observable
  accessor editScopeChangeCustomItems: EditScopeChangeCustomItemViewModel[] = []

  readonly scopeChangesValidationStep = validationContext(this, {
    editScopeChangeCustomItems: [
      validateElements('Some scope changes are invalid.'),
    ],
  })

  constructor(
    private readonly _scopeChangeRequestStore: ScopeChangeRequestStore,
    private readonly _onSave: () => Promise<void>,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Opens the dialog
   * @param scopeChangeRequest
   */
  @action.bound
  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest
    this.editScopeChangeCustomItems = scopeChangeRequest.customItems.map(
      EditScopeChangeCustomItemViewModel.fromCustomScopeChangeItem,
    )
    this.modal.show()
  }

  /**
   * Edit the scope change request and close the dialog
   */
  save = task.resolved(async () => {
    if (!this.scopeChangesValidationStep.validate().isValid) {
      return
    }

    await this._scopeChangeRequestStore
      .editScopeChangeRequest({
        scopeChangeRequestId: this.scopeChangeRequest.id,
        customItems: this.editScopeChangeCustomItems.map((i) => ({
          name: i.name,
          quantity: i.quantity,
          price: i.price,
          proPrice: i.proPrice,
        })),
        inventoryItems: this.scopeChangeRequest.inventoryItems.map((i) => ({
          versionId: i.inventoryItemVersionId,
          quantity: i.quantity,
        })),
      })
      .then(() =>
        this._scopeChangeRequestStore.fetchScopeChangeRequest(
          this.scopeChangeRequest.id,
        ),
      )
      .catch(this._createAlertErrorHandler('Updating the items failed'))

    this._newAlert()
      .withState('success')
      .saying('Success', 'Scope change items have been updated')
      .autoDismiss(2000)
      .show()

    void this._onSave()
    this.modal.close()
  })

  /**
   * Adds a new custom scope change
   */
  @action.bound
  addScopeChange() {
    this.editScopeChangeCustomItems.push(
      new EditScopeChangeCustomItemViewModel({
        name: '',
        quantity: 1,
        price: 0,
        proPrice: 0,
      }),
    )
  }

  /**
   * Removes a previously added custom scope change.
   *
   * @param key
   */
  @action.bound
  removeScopeChange(key: string) {
    const pos = this.editScopeChangeCustomItems.findIndex(
      (item) => item.key === key,
    )
    this.editScopeChangeCustomItems.splice(pos, 1)
  }
}

/**
 * Initial values of an edited custom scope change item.
 */
export interface EditScopeChangeCustomItemViewModelProps {
  name: string
  quantity: number
  price: number
  proPrice: number
}

/**
 * View Model for a editing a scope change item.
 */
export class EditScopeChangeCustomItemViewModel {
  readonly key = randomUUID()

  @observable accessor name = ''

  @observable accessor quantity = 1

  @observable accessor price = 0

  @observable accessor proPrice = 0

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    name: [required('Service name is required.')],
    quantity: [
      (opts) =>
        opts.value >= 1 || opts.value <= -1 || 'Quantity cannot be zero.',
    ],
    price: [(opts) => opts.value >= 0 || 'Price cannot be negative'],
    proPrice: [(opts) => opts.value >= 0 || 'Pro price cannot be negative'],
  })

  constructor({
    name,
    quantity,
    price,
    proPrice,
  }: EditScopeChangeCustomItemViewModelProps) {
    this.name = name
    this.quantity = quantity
    this.price = price
    this.proPrice = proPrice
  }

  @action.bound
  setQuantity(value: number | null) {
    this.quantity = value === null || value === 0 ? 1 : value
  }

  @action.bound
  incrementQuantity() {
    this.quantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.quantity === 1) {
      return
    }

    this.quantity -= 1
  }

  @action.bound
  setName(value: string) {
    this.name = value
  }

  static fromCustomScopeChangeItem(item: ScopeChangeRequestItem) {
    return new EditScopeChangeCustomItemViewModel({
      name: item.name,
      quantity: item.quantity,
      price: item.price,
      proPrice: item.proPrice,
    })
  }
}
